import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";
import {
  resolveLastUpdated,
  describeAge,
  useAgeTicker,
} from "../util/CameraAgeUtil";

const API_HOST = process.env.REACT_APP_API_HOST || "/lmiga";
const REFRESH_INTERVAL_MS = 30_000;

/**
 * Standalone page to display a full-sized camera image with direction controls.
 * URL: /showCamera?id=CAMERA_ID&direction=N
 *
 * Replaces the old showCamera.jsp popup. Linked from cameraInfo.csv.
 */
export default function ShowCamera() {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const cameraId = searchParams.get("id") || "";
  const directionParam = searchParams.get("direction") || "";

  const [cameraData, setCameraData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDirection, setSelectedDirection] = useState(directionParam);
  const [imageKey, setImageKey] = useState(0); // forces image reload on refresh

  // Fetch camera metadata
  const fetchCamera = useCallback(async () => {
    if (!cameraId) {
      setError("No camera ID provided");
      setLoading(false);
      return;
    }
    try {
      const response = await fetch(
        `${API_HOST}/showCamera.json?id=${encodeURIComponent(cameraId)}`,
      );
      if (!response.ok) {
        if (response.status === 404) {
          setError(`Camera "${cameraId}" not found`);
        } else {
          setError(`Error loading camera: ${response.statusText}`);
        }
        setLoading(false);
        return;
      }
      const data = await response.json();
      setCameraData(data);
      // Set initial direction: use URL param if valid, else first direction
      if (
        directionParam &&
        data.directions.some((d) => d.code === directionParam)
      ) {
        setSelectedDirection(directionParam);
      } else if (data.directions.length > 0) {
        setSelectedDirection(data.directions[0].code);
      }
      setLoading(false);
    } catch (err) {
      setError(`Failed to load camera: ${err.message}`);
      setLoading(false);
    }
  }, [cameraId, directionParam]);

  useEffect(() => {
    fetchCamera();
  }, [fetchCamera]);

  // Periodic refresh of image and age
  useEffect(() => {
    if (!cameraData) return;
    const interval = setInterval(() => {
      setImageKey((k) => k + 1);
      fetchCamera();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [cameraData, fetchCamera]);

  useAgeTicker(1000);

  const handleDirectionChange = (dirCode) => {
    setSelectedDirection(dirCode);
    setSearchParams({ id: cameraId, direction: dirCode });
    setImageKey((k) => k + 1);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          bgcolor: "background.default",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          bgcolor: "background.default",
        }}
      >
        <Typography color="error" variant="h6">
          {error}
        </Typography>
      </Box>
    );
  }

  const currentDir = cameraData.directions.find(
    (d) => d.code === selectedDirection,
  );
  const ageText = currentDir
    ? describeAge(resolveLastUpdated(currentDir)).text
    : "Unknown";

  // Build image URL
  const encodedId = encodeURIComponent(cameraData.id);
  let imageUrl;
  if (currentDir && currentDir.remoteUrl) {
    imageUrl = currentDir.remoteUrl;
  } else if (selectedDirection && selectedDirection !== "NONE") {
    imageUrl = `${API_HOST}/snapshot?id=${encodedId}&direction=${selectedDirection}`;
  } else {
    imageUrl = `${API_HOST}/snapshot?id=${encodedId}`;
  }
  // Append cache-buster
  imageUrl += (imageUrl.includes("?") ? "&" : "?") + `_t=${imageKey}`;

  const referrerPolicy = imageUrl.includes("cctv.travelmidwest.com")
    ? "origin"
    : "no-referrer";

  const dirDisplayName =
    currentDir && currentDir.displayName !== "None"
      ? ` (${currentDir.displayName})`
      : "";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        height: "100vh",
        overflow: "hidden",
        bgcolor: "background.default",
        color: "text.primary",
        p: 1,
      }}
    >
      {/* Header info */}
      <Typography
        variant="subtitle1"
        sx={{
          textAlign: "center",
          mb: 1,
          fontWeight: 500,
          maxWidth: 900,
        }}
      >
        {cameraData.locationDescription}
        {dirDisplayName} ({cameraData.id}) ({cameraData.sourceName}) Last
        Updated: {ageText}
      </Typography>

      {/* Camera image */}
      <Box
        sx={{
          flex: 1,
          width: "100%",
          minHeight: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mb: 1,
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.03)"
              : "rgba(0,0,0,0.02)",
          borderRadius: 1,
        }}
      >
        <img
          src={imageUrl}
          alt={`${cameraData.locationDescription} - ${selectedDirection}`}
          referrerPolicy={referrerPolicy}
          style={{
            width: "100%",
            height: "100%",
            display: "block",
            objectFit: "contain",
          }}
        />
      </Box>

      {/* Direction buttons */}
      {cameraData.directions.length > 1 && (
        <ButtonGroup variant="outlined" size="large">
          {cameraData.directions.map((dir) => (
            <Button
              key={dir.code}
              onClick={() => handleDirectionChange(dir.code)}
              variant={
                dir.code === selectedDirection ? "contained" : "outlined"
              }
            >
              {dir.displayName}
            </Button>
          ))}
        </ButtonGroup>
      )}
    </Box>
  );
}

